import React, { useState } from 'react';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Please fill required fields'); return; }
    setSending(true);
    try {
      await api.post('/api/support/tickets', { ...form, type: 'contact' });
      setSent(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        <section className="max-w-[900px] mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-12">
            <h1 className="news-masthead text-4xl font-bold">Contact Us</h1>
            <p className="text-[#5B616E] mt-3 max-w-lg mx-auto">Have a story tip, feedback, or inquiry? We'd love to hear from you.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              { icon: Mail, title: 'Tips and support', detail: 'support@neuralsignal.co.uk' },
              { icon: Mail, title: 'Privacy', detail: 'privacy@neuralsignal.co.uk' },
            ].map(({ icon: Icon, title, detail }) => (
              <Card key={title} className="bg-white border-[#E7E0D6] text-center">
                <CardContent className="pt-6">
                  <div className="w-10 h-10 rounded-xl bg-[#1E5A8A15] flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-5 h-5 text-[#1E5A8A]" />
                  </div>
                  <h3 className="font-medium text-sm">{title}</h3>
                  <p className="text-xs text-[#5B616E] mt-1">{detail}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {sent ? (
            <Card className="bg-white border-[#E7E0D6] text-center p-12">
              <CheckCircle className="w-16 h-16 text-[#1F7A5A] mx-auto mb-4" />
              <h2 className="text-xl font-bold">Message Received</h2>
              <p className="text-[#5B616E] mt-2">Thank you for reaching out. Our team will respond within 24 hours.</p>
              <Button variant="outline" className="mt-6" onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}>Send Another</Button>
            </Card>
          ) : (
            <Card className="bg-white border-[#E7E0D6]">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium block mb-1">Name *</label>
                      <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white" />
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1">Email *</label>
                      <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Subject</label>
                    <input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white" placeholder="Story tip, feedback, inquiry..." />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Message *</label>
                    <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} required rows={6} className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white resize-none" />
                  </div>
                  <Button type="submit" className="w-full bg-[#121316] text-white hover:bg-[#1B1D22]" disabled={sending}>
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
